import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import { isAdminOrSuperAdmin } from '../utils/auth';
import '../App.css';

interface Field {
  id: string;
  name: string;
  address: string;
  city: string;
  state: string;
  zipCode: string;
  description?: string;
  picture?: string;
  map?: string;
  website?: string;
  phoneNumber?: string;
  email?: string;
  hoursOfOperation?: string;
  parkingInformation?: string;
  collectionId?: string;
  collection?: { id: string; name: string } | null;
}

const FieldDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const { user } = useAuth();
  const [field, setField] = useState<Field | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchField = async () => {
      try {
        const response = await api.get(`/fields/${id}`);
        setField(response.data);
      } catch (error) {
        console.error('Error fetching field:', error);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      fetchField();
    }
  }, [id]);

  if (loading) {
    return <LoadingSpinner fullPage />;
  }

  if (!field) {
    return <div className="container">Field not found</div>;
  }

  return (
    <div className="container">
      <div className="card">
        <div style={{ marginBottom: '20px' }}>
          <Link to="/fields" style={{ color: '#2E7D32', textDecoration: 'none' }}>
            ← Back to Fields
          </Link>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '20px' }}>
          <div>
            <h1 style={{ margin: 0 }}>{field.name}</h1>
            {field.collection && (
              <p style={{ margin: '5px 0', color: '#757575' }}>
                <span className="badge badge-info">{field.collection.name}</span>
              </p>
            )}
            <p style={{ color: '#757575', margin: '10px 0' }}>
              {field.address}<br />
              {field.city}, {field.state} {field.zipCode}
            </p>
          </div>
          {isAdminOrSuperAdmin(user?.profileType) && (
            <Link to="/fields" className="btn btn-primary">
              Manage Fields
            </Link>
          )}
        </div>

        {field.picture && (
          <div style={{ marginBottom: '30px' }}>
            <img
              src={field.picture}
              alt={field.name}
              style={{ width: '100%', maxHeight: '500px', objectFit: 'cover', borderRadius: '8px' }}
            />
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px', marginBottom: '30px' }}>
          <div>
            <h2>Field Information</h2>
            {field.description && (
              <p style={{ color: '#757575', lineHeight: '1.6' }}>{field.description}</p>
            )}

            <div style={{ marginTop: '20px' }}>
              <h3>Contact Information</h3>
              {field.phoneNumber && (
                <p>
                  <strong>Phone:</strong>{' '}
                  <a href={`tel:${field.phoneNumber}`} style={{ color: '#2E7D32' }}>
                    {field.phoneNumber}
                  </a>
                </p>
              )}
              {field.email && (
                <p>
                  <strong>Email:</strong>{' '}
                  <a href={`mailto:${field.email}`} style={{ color: '#2E7D32' }}>
                    {field.email}
                  </a>
                </p>
              )}
              {field.website && (
                <p>
                  <strong>Website:</strong>{' '}
                  <a href={field.website} target="_blank" rel="noopener noreferrer" style={{ color: '#2E7D32' }}>
                    {field.website}
                  </a>
                </p>
              )}
            </div>
          </div>

          <div>
            {field.hoursOfOperation && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Hours of Operation</h3>
                <p style={{ color: '#757575', whiteSpace: 'pre-line' }}>{field.hoursOfOperation}</p>
              </div>
            )}

            {field.parkingInformation && (
              <div style={{ marginBottom: '20px' }}>
                <h3>Parking Information</h3>
                <p style={{ color: '#757575', whiteSpace: 'pre-line' }}>{field.parkingInformation}</p>
              </div>
            )}
          </div>
        </div>

        {field.map && (
          <div style={{ marginTop: '30px' }}>
            <h2>Field Map</h2>
            <img
              src={field.map}
              alt={`Map of ${field.name}`}
              style={{ width: '100%', maxHeight: '600px', objectFit: 'contain', borderRadius: '8px', border: '1px solid #E0E0E0' }}
            />
          </div>
        )}

        <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#F5F5F5', borderRadius: '8px' }}>
          <h3>Location</h3>
          <p style={{ margin: '5px 0' }}>
            <strong>Address:</strong> {field.address}
          </p>
          <p style={{ margin: '5px 0' }}>
            <strong>City:</strong> {field.city}
          </p>
          <p style={{ margin: '5px 0' }}>
            <strong>State:</strong> {field.state}
          </p>
          <p style={{ margin: '5px 0' }}>
            <strong>Zip Code:</strong> {field.zipCode}
          </p>
          <div style={{ marginTop: '15px' }}>
            <a
              href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(`${field.address}, ${field.city}, ${field.state} ${field.zipCode}`)}`}
              target="_blank"
              rel="noopener noreferrer"
              className="btn btn-primary"
            >
              View on Google Maps
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldDetail;

